/* eslint-disable consistent-return,max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/es';
import SweetAlert from 'react-bootstrap-sweetalert';
// @material-ui/icons
import Check from '@material-ui/icons/Check';
import ArrowUp from '@material-ui/icons/ArrowUpward';
import SupervisedUser from '@material-ui/icons/SupervisedUserCircle';
import Clear from '@material-ui/icons/Close';
// @material-ui/core components
import FilterStyles from 'assets/jss/AdsFilter';
import Checkbox from '@material-ui/core/Checkbox/Checkbox';
import withStyles from '@material-ui/core/styles/withStyles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
// our components
import { creditTypes } from 'config';
import Datepicker from 'components/common/DatePicker';
import SectionDivider from 'components/common/SectionDivider/SectionDivider';
import UserSelect from 'components/User/Select';
import WithPermission from 'components/User/WithPermission';

const defaultCreditTypes = [creditTypes.INCREMENT, creditTypes.DECREMENT];

/**
 * @class
 * @extends React.Component
 */
class CreditFilter extends React.Component {
  constructor(props) {
    super(props);
    const { appliedFilters } = props;
    this.state = {
      selectedCreditTypes:
        (appliedFilters && appliedFilters.creditType) || defaultCreditTypes,
      localFilter: appliedFilters || null,
      showSelectUser: false,
    };
  }

  /**
   * @description
   * Change filters.
   *
   * @param {Object} changedFilter - Changed filters values.
   */
  changeLocalFilter = changedFilter => {
    const { localFilter } = this.state;
    this.setState({ localFilter: { ...localFilter, ...changedFilter } });
  };

  /**
   * @description
   * Handle select creditTypes checkboxes.
   *
   * @param {string} checkLabel - CreditTypes selected.
   * @param {string} selectedState - CreditTypes local state name.
   * @param {string} filterName - CreditTypes filter name.
   * @returns {undefined} No return.
   */
  handleCheck = (checkLabel, selectedState, filterName) => {
    // eslint-disable-next-line react/destructuring-assignment
    const state = this.state[selectedState];
    let finalState = [];

    // if exists, remove from list
    if (state.indexOf(checkLabel) !== -1) {
      finalState = state.filter(label => label !== checkLabel);
    } else {
      // otherwise, add it
      finalState = [...state, checkLabel];
    }

    this.setState({ [selectedState]: finalState }, () =>
      this.changeLocalFilter(
        finalState.length === 0
          ? { [filterName]: undefined }
          : { [filterName]: finalState },
      ),
    );
  };

  /**
   * @description
   * Call filter callback.
   */
  handleFilter = () => {
    const { onFilter } = this.props;
    const { localFilter } = this.state;
    onFilter({ ...localFilter });
  };

  /**
   * @description
   * Render full filter's box.
   * @returns {*} - AdFilter's box.
   */
  render() {
    const { classes, onRestartFilters, t, onShowFilters } = this.props;
    const { selectedCreditTypes, localFilter, showSelectUser } = this.state;

    return (
      <div className={classes.expandFilters} style={{ marginBottom: 20 }}>
        <GridContainer>
          <WithPermission
            matchAll
            allowPermissions={['list_user', 'list_credittransaction']}
          >
            <GridItem xs={12} md={4}>
              <SectionDivider label={t('credit.filters.user')}>
                <div
                  style={{
                    padding: 10,
                  }}
                >
                  <div
                    className={`${classes.userSelect} ${
                      localFilter.user ? classes.userSelected : ''
                    }`}
                  >
                    <Button
                      simple
                      color="info"
                      onClick={() => {
                        this.setState({
                          showSelectUser: !showSelectUser,
                        });
                      }}
                    >
                      <div>
                        <p className="title">{t('credit.filters.user')}</p>
                        <p className="user">
                          {localFilter.userName
                            ? localFilter.userName
                            : t(
                                'payment.list.filters.section_id.agency.placeholder',
                              )}
                        </p>
                      </div>
                    </Button>

                    {localFilter.userName ? (
                      <Button
                        simple
                        onClick={() => {
                          this.changeLocalFilter({
                            user: undefined,
                            userName: undefined,
                          });
                        }}
                      >
                        <Clear />
                      </Button>
                    ) : (
                      <span>
                        <SupervisedUser />
                      </span>
                    )}
                  </div>
                </div>
                <SweetAlert
                  title={t('common.select_user')}
                  showConfirm={false}
                  showCancel={false}
                  onConfirm={() => {}}
                  customClass={classes.adModalConfirmation}
                  show={showSelectUser}
                  closeOnClickOutside
                  onCancel={() =>
                    this.setState({
                      showSelectUser: !showSelectUser,
                    })
                  }
                >
                  <UserSelect
                    selectUser={user => {
                      this.setState({
                        localFilter: {
                          ...localFilter,
                          user: user.id,
                          userName: user.fullname,
                        },
                        showSelectUser: !showSelectUser,
                      });
                    }}
                  />
                </SweetAlert>
              </SectionDivider>
            </GridItem>
          </WithPermission>
          <GridItem xs={12} md={3}>
            <SectionDivider label={t('credit.filters.type')}>
              <div
                style={{
                  padding: '10px 16px',
                }}
              >
                {Object.keys(creditTypes).map(cType => (
                  <FormControlLabel
                    key={cType}
                    control={
                      <Checkbox
                        onClick={() =>
                          this.handleCheck(
                            cType,
                            'selectedCreditTypes',
                            'creditType',
                          )
                        }
                        checked={selectedCreditTypes.indexOf(cType) !== -1}
                        checkedIcon={<Check className={classes.checkedIcon} />}
                        icon={<Check className={classes.uncheckedIcon} />}
                        className={classes.checked}
                      />
                    }
                    id={cType}
                    label={t(`credit.list.type.${cType}`)}
                    data-testid={`credit-type-${cType}`}
                    style={{ width: '50%' }}
                  />
                ))}
              </div>
            </SectionDivider>
          </GridItem>
          <GridItem xs={12} md={5}>
            <SectionDivider label={t('credit.filters.date')}>
              <div style={{ padding: 10 }}>
                <GridContainer>
                  <GridItem xs={12} sm={12} md={6}>
                    <Datepicker
                      title={t('paidad.filters.date.from')}
                      defaultValue={t('paidad.filters.date.from_placeholder')}
                      onChange={date => {
                        if (localFilter.createdOnLte < date) {
                          return;
                        }
                        this.changeLocalFilter({
                          createdOnGte: date
                            ? moment(date)
                                .startOf('day')
                                .format()
                            : undefined,
                        });
                      }}
                      value={
                        localFilter.createdOnGte !== undefined
                          ? moment(localFilter.createdOnGte)
                          : ''
                      }
                      validGte={localFilter.createdOnLte}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={6}>
                    <Datepicker
                      title={t('paidad.filters.date.to')}
                      defaultValue={t('paidad.filters.date.to_placeholder')}
                      onChange={date => {
                        if (localFilter.createdOnGte > date) {
                          return;
                        }
                        this.changeLocalFilter({
                          createdOnLte: date
                            ? moment(date)
                                .endOf('day')
                                .format()
                            : undefined,
                        });
                      }}
                      value={
                        localFilter.createdOnLte !== undefined
                          ? moment(localFilter.createdOnLte)
                          : ''
                      }
                      validLte={localFilter.createdOnGte}
                    />
                  </GridItem>
                </GridContainer>
              </div>
            </SectionDivider>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={12} md={6}>
            <Button
              color="primary"
              simple
              className={classes.buttons}
              onClick={onShowFilters}
              id="hideFilters"
              key="hideFilters"
            >
              <ArrowUp />
              {t('ad.filters.shrink')}
            </Button>
          </GridItem>
          <GridItem xs={12} sm={12} md={6}>
            <GridContainer justify="flex-end">
              <GridItem>
                <Button
                  color="danger"
                  simple
                  className={classes.buttons}
                  onClick={() => {
                    this.setState({
                      selectedCreditTypes: defaultCreditTypes,
                      localFilter: {},
                    });
                    onRestartFilters();
                  }}
                  data-testid="restartFilters"
                  key="restartFilters"
                >
                  {t('ad.filters.clean')}
                </Button>
                <Button
                  data-cy="filterAds"
                  color="primary"
                  className={classes.buttons}
                  onClick={() => {
                    this.handleFilter();
                    onShowFilters();
                  }}
                  data-testid="filterAds"
                  key="filterAds"
                >
                  {t('ad.filters.filter')}
                </Button>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

/**
 * CreditFilter PropTypes
 * @param classes - styles received from withStyles
 * @param appliedFilters - prop to know applied filters
 * @param t - prop from HOC with string translations
 * @callback onFilter - callback called when user clicks filter button
 * @callback onShowFilters - callback called when user clicks filters button
 * @callback onRestartFilters - callback called when user restart filters button
 */

CreditFilter.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  appliedFilters: PropTypes.shape({}).isRequired,
  onFilter: PropTypes.func.isRequired,
  onShowFilters: PropTypes.func.isRequired,
  onRestartFilters: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(withStyles(FilterStyles)(CreditFilter));
